import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, Clock, Save, CheckCircle2, FileSpreadsheet, FileDown } from 'lucide-react';
import SupervisorSignOff from './SupervisorSignOff';

const EMPTY_LOG = {
  week_start: '', week_end: '', date_worked: '', start_time: '', end_time: '',
  break_minutes: 0, total_hours: 0, supervision_hours: 0, direct_client_hours: 0, activities: '',
  population_served: '', practice_skills: '', reflection: '', notes: '', status: 'draft',
  category: '', supervisor_name: '',
};

const calcHours = (start, end, breakMin) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm) - (Number(breakMin) || 0);
  return Math.round((mins / 60) * 100) / 100;
};

const PortalHours = ({ meta }) => {
  const [tracker, setTracker] = useState({});
  const [summary, setSummary] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTracker, setSavingTracker] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_LOG);
  const [savingLog, setSavingLog] = useState(false);
  const [exporting, setExporting] = useState('');

  const downloadFile = async (path, filename, key) => {
    setExporting(key);
    try {
      const res = await axios.get(`${API_URL}${path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not export right now. Please try again.'); }
    finally { setExporting(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, lg] = await Promise.all([
        axios.get(`${API_URL}/internship-portal/tracker`),
        axios.get(`${API_URL}/internship-portal/logs`),
      ]);
      setTracker(tr.data.tracker || {});
      setSummary(tr.data.summary || {});
      setLogs(lg.data.logs || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setT = (k, v) => setTracker((f) => ({ ...f, [k]: v }));
  const setF = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (['start_time', 'end_time', 'break_minutes'].includes(k)) {
      const auto = calcHours(next.start_time, next.end_time, next.break_minutes);
      if (auto !== null) next.total_hours = auto;
    }
    return next;
  });

  // Client-side guard mirroring the backend, for instant inline feedback.
  const logError = (() => {
    if (!form.date_worked) return 'Enter the date you worked.';
    const hasTimes = form.start_time && form.end_time;
    const brk = Number(form.break_minutes) || 0;
    if (brk < 0) return 'Break minutes cannot be negative.';
    if (hasTimes) {
      const [sh, sm] = form.start_time.split(':').map(Number);
      const [eh, em] = form.end_time.split(':').map(Number);
      const gross = (eh * 60 + em) - (sh * 60 + sm);
      if (gross <= 0) return 'End time must be after start time.';
      if (brk >= gross) return "Unpaid break can't be as long as the shift.";
      if ((gross - brk) <= 0) return 'This shift works out to zero hours after the break.';
    } else if ((Number(form.total_hours) || 0) < 0) {
      return 'Total hours cannot be negative.';
    }
    const total = Number(form.total_hours) || 0;
    if (total > 0 && (Number(form.supervision_hours) || 0) > total) return "Supervision hours can't exceed total hours.";
    if (total > 0 && (Number(form.direct_client_hours) || 0) > total) return "Direct-client hours can't exceed total hours.";
    return null;
  })();

  const saveTracker = async () => {
    setSavingTracker(true);
    try {
      const res = await axios.put(`${API_URL}/internship-portal/tracker`, {
        placement_title: tracker.placement_title, agency_name: tracker.agency_name,
        location: tracker.location,
        required_hours: tracker.required_hours ? Number(tracker.required_hours) : null,
        weekly_hours: tracker.weekly_hours ? Number(tracker.weekly_hours) : null,
        start_date: tracker.start_date, end_date: tracker.end_date,
      });
      setSummary(res.data.summary || {});
      toast.success('Tracker saved.');
    } catch (e) { toast.error('Could not save tracker.'); } finally { setSavingTracker(false); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY_LOG); setLogOpen(true); };
  const openEdit = (l) => { setEditing(l.log_id); setForm({ ...EMPTY_LOG, ...l }); setLogOpen(true); };

  const saveLog = async (markComplete) => {
    if (logError) { toast.error(logError); return; }
    setSavingLog(true);
    const payload = { ...form, status: markComplete ? 'complete' : form.status,
      total_hours: Number(form.total_hours) || 0, supervision_hours: Number(form.supervision_hours) || 0,
      direct_client_hours: Number(form.direct_client_hours) || 0,
      break_minutes: Number(form.break_minutes) || 0 };
    try {
      if (editing) await axios.put(`${API_URL}/internship-portal/logs/${editing}`, payload);
      else await axios.post(`${API_URL}/internship-portal/logs`, payload);
      toast.success(editing ? 'Log updated.' : 'Log saved.');
      setLogOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save log.'); } finally { setSavingLog(false); }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this log entry?')) return;
    try { await axios.delete(`${API_URL}/internship-portal/logs/${id}`); await load(); toast.success('Deleted.'); }
    catch (e) { toast.error('Could not delete.'); }
  };

  const toggleComplete = async (l) => {    try {
      await axios.put(`${API_URL}/internship-portal/logs/${l.log_id}`, {
        ...l, status: l.status === 'complete' ? 'draft' : 'complete',
      });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update.'); }
  };

  if (loading) return <div className="py-16 text-center text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="portal-hours">
      {/* Tracker settings + progress */}
      <div className="bg-white rounded-2xl border p-6">
        <h3 className="font-serif text-lg font-bold text-primary mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-secondary" /> Required Hours Tracker</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Placement Title"><Input value={tracker.placement_title || ''} onChange={(e) => setT('placement_title', e.target.value)} data-testid="tracker-title" /></Field>
          <Field label="Agency Name"><Input value={tracker.agency_name || ''} onChange={(e) => setT('agency_name', e.target.value)} data-testid="tracker-agency" /></Field>
          <Field label="Location"><Input value={tracker.location || ''} onChange={(e) => setT('location', e.target.value)} data-testid="tracker-location" /></Field>
          <Field label="Total Required Hours"><Input type="number" value={tracker.required_hours || ''} onChange={(e) => setT('required_hours', e.target.value)} data-testid="tracker-required" /></Field>
          <Field label="Weekly Hour Requirement"><Input type="number" value={tracker.weekly_hours || ''} onChange={(e) => setT('weekly_hours', e.target.value)} data-testid="tracker-weekly" /></Field>
          <div />
          <Field label="Start Date"><Input type="date" value={tracker.start_date || ''} onChange={(e) => setT('start_date', e.target.value)} data-testid="tracker-start" /></Field>
          <Field label="End Date"><Input type="date" value={tracker.end_date || ''} onChange={(e) => setT('end_date', e.target.value)} data-testid="tracker-end" /></Field>
        </div>
        <div className="mt-4"><Button onClick={saveTracker} disabled={savingTracker} className="bg-primary text-white" data-testid="save-tracker-btn"><Save className="w-4 h-4 mr-2" /> {savingTracker ? 'Saving…' : 'Save Tracker'}</Button></div>

        <div className="mt-6 border-t pt-5">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-semibold text-primary">{summary.percent_complete || 0}% complete</span>
            <span className="text-gray-500">{summary.completed_hours || 0} / {summary.required_hours || 0} hrs</span>
          </div>
          <Progress value={summary.percent_complete || 0} className="h-3" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-center">
            <Metric label="Completed" value={summary.completed_hours || 0} />
            <Metric label="Remaining" value={summary.remaining_hours || 0} />
            <Metric label={summary.supervision_required > 0 ? 'Supervision' : 'Supervision'} value={summary.supervision_required > 0 ? `${summary.supervision_hours || 0} / ${summary.supervision_required}` : (summary.supervision_hours || 0)} small={summary.supervision_required > 0} />
            <Metric label="Est. Completion" value={summary.expected_completion || '—'} small />
          </div>
          {(summary.direct_client_required > 0 || summary.min_months > 0) && (
            <div className="grid grid-cols-2 gap-3 mt-3 text-center" data-testid="tracker-extra-metrics">
              {summary.direct_client_required > 0 && <Metric label="Direct Client" value={`${summary.direct_client_hours || 0} / ${summary.direct_client_required}`} small />}
              {summary.min_months > 0 && <Metric label="Months Elapsed" value={`${summary.months_elapsed || 0} / ${summary.min_months}`} small />}
            </div>
          )}
        </div>
      </div>

      {/* Weekly logs */}
      <div className="bg-white rounded-2xl border p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-serif text-lg font-bold text-primary">Weekly Hour Logs</h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadFile('/internship-portal/export/logs.csv', 'internship_hours.csv', 'csv')} disabled={exporting === 'csv'} data-testid="export-csv-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}</Button>
            <Button size="sm" variant="outline" onClick={() => downloadFile('/internship-portal/export/report.pdf', 'internship_hours_report.pdf', 'pdf')} disabled={exporting === 'pdf'} data-testid="export-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> {exporting === 'pdf' ? 'Preparing…' : 'PDF Report'}</Button>
            <Button size="sm" className="bg-secondary text-white" onClick={openNew} data-testid="add-log-btn"><Plus className="w-4 h-4 mr-1" /> Add Log</Button>
          </div>
        </div>
        {logs.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">No logs yet. Add your first weekly entry. Only completed logs count toward your total hours.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Hours</th><th className="py-2 pr-3">Supv.</th>
                <th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Activities</th><th className="py-2 pr-3">Status</th><th className="py-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.log_id} className="border-b last:border-0" data-testid={`log-row-${l.log_id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{l.date_worked || l.week_start || '—'}</td>
                    <td className="py-2 pr-3 font-medium">{l.total_hours}</td>
                    <td className="py-2 pr-3">{l.supervision_hours || 0}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{l.category || '—'}</td>
                    <td className="py-2 pr-3 max-w-[200px] truncate">{l.activities || '—'}</td>
                    <td className="py-2 pr-3">
                      <button onClick={() => toggleComplete(l)} data-testid={`toggle-log-${l.log_id}`}>
                        <Badge className={l.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>{l.status}</Badge>
                      </button>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="text-gray-400 hover:text-primary p-1" onClick={() => openEdit(l)} data-testid={`edit-log-${l.log_id}`}><Pencil className="w-4 h-4" /></button>
                      <button className="text-gray-400 hover:text-red-600 p-1" onClick={() => deleteLog(l.log_id)} data-testid={`delete-log-${l.log_id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SupervisorSignOff />

      {/* Log dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Weekly Log' : 'Add Weekly Log'}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Week Beginning"><Input type="date" value={form.week_start} onChange={(e) => setF('week_start', e.target.value)} data-testid="log-week-start" /></Field>
            <Field label="Week Ending"><Input type="date" value={form.week_end} onChange={(e) => setF('week_end', e.target.value)} /></Field>
            <Field label="Date Worked"><Input type="date" value={form.date_worked} onChange={(e) => setF('date_worked', e.target.value)} data-testid="log-date" /></Field>
            <div />
            <Field label="Start Time"><Input type="time" value={form.start_time} onChange={(e) => setF('start_time', e.target.value)} /></Field>
            <Field label="End Time"><Input type="time" value={form.end_time} onChange={(e) => setF('end_time', e.target.value)} /></Field>
            <Field label="Break (minutes)"><Input type="number" value={form.break_minutes} onChange={(e) => setF('break_minutes', e.target.value)} /></Field>
            <Field label="Total Hours"><Input type="number" step="0.25" value={form.total_hours} onChange={(e) => setF('total_hours', e.target.value)} data-testid="log-total-hours" /></Field>
            <Field label="Supervision Hours"><Input type="number" step="0.25" value={form.supervision_hours} onChange={(e) => setF('supervision_hours', e.target.value)} data-testid="log-supervision-hours" /></Field>
            <Field label="Direct Client Hours"><Input type="number" step="0.25" value={form.direct_client_hours} onChange={(e) => setF('direct_client_hours', e.target.value)} data-testid="log-direct-client-hours" /></Field>
            <Field label="Primary Category">
              <Select value={form.category || 'none'} onValueChange={(v) => setF('category', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="log-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(meta.log_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Supervisor (for this entry)"><Input value={form.supervisor_name || ''} onChange={(e) => setF('supervisor_name', e.target.value)} placeholder="e.g., Jane Doe, LCSW-S" data-testid="log-supervisor" /></Field>
            <Field label="Population Served">
              <Select value={form.population_served || 'none'} onValueChange={(v) => setF('population_served', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="log-population"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(meta.populations || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {logError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-1" data-testid="log-validation-error">{logError}</p>
          )}
          <Field label="Activities Completed"><Textarea rows={2} value={form.activities} onChange={(e) => setF('activities', e.target.value)} data-testid="log-activities" /></Field>
          <Field label="Practice Skills Used"><Textarea rows={2} value={form.practice_skills} onChange={(e) => setF('practice_skills', e.target.value)} /></Field>
          <Field label="Student Reflection"><Textarea rows={2} value={form.reflection} onChange={(e) => setF('reflection', e.target.value)} /></Field>
          <Field label="Additional Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setF('notes', e.target.value)} /></Field>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => saveLog(false)} disabled={savingLog || !!logError} data-testid="save-draft-btn"><Save className="w-4 h-4 mr-1" /> Save Draft</Button>
            <Button className="bg-primary text-white" onClick={() => saveLog(true)} disabled={savingLog || !!logError} data-testid="save-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark Complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="text-xs text-gray-500 mb-1 block">{label}</Label>{children}</div>
);
const Metric = ({ label, value, small }) => (
  <div className="bg-gray-50 rounded-xl p-3">
    <p className={`font-bold text-primary ${small ? 'text-sm' : 'text-xl'}`}>{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

export default PortalHours;
